import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  Supplier,
  CreateSupplierRequest,
  UpdateSupplierRequest,
} from 'types/supplier';

interface PaginatedListResponse<T> {
  data: T[];
  meta: {
    totalPages: number;
  };
}

type PaginatedListClient = {
  get<T>(route: string, params?: Record<string, string>): Promise<T>;
};

async function fetchAllPaginatedSuppliers(
  apiClient: PaginatedListClient,
  queryParams: Record<string, string>,
): Promise<Supplier[]> {
  const all: Supplier[] = [];
  let page = 1;

  while (true) {
    const res = await apiClient.get<PaginatedListResponse<Supplier>>(
      API_ROUTES.SUPPLIERS,
      {
        ...queryParams,
        page: String(page),
        limit: '100',
      },
    );
    all.push(...res.data);
    if (page >= res.meta.totalPages) {
      break;
    }
    page += 1;
  }

  return all;
}

/**
 * Supplier API service
 * Consolidates all supplier-related API calls in a single location
 */
export function useSupplierService() {
  const apiClient = useApiClient();

  /**
   * Get all suppliers
   * @param includeVoided - Optional flag to include voided suppliers (default: false)
   */
  const getAllSuppliers = useCallback(
    async (includeVoided = false): Promise<Supplier[]> => {
      const queryParams: Record<string, string> = {};
      if (includeVoided) {
        queryParams.includeVoided = 'true';
      }
      return fetchAllPaginatedSuppliers(apiClient, queryParams);
    },
    [apiClient],
  );

  /**
   * Get a single supplier by ID
   */
  const getSupplierById = useCallback(
    async (id: string): Promise<Supplier> => {
      return apiClient.get<Supplier>(`${API_ROUTES.SUPPLIERS}/${id}`);
    },
    [apiClient],
  );

  /**
   * Create a new supplier
   */
  const createSupplier = useCallback(
    async (supplier: CreateSupplierRequest): Promise<Supplier> => {
      return apiClient.post<Supplier>(
        API_ROUTES.SUPPLIERS,
        supplier as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Update a supplier
   */
  const updateSupplier = useCallback(
    async (id: string, supplier: UpdateSupplierRequest): Promise<Supplier> => {
      return apiClient.patch<Supplier>(
        `${API_ROUTES.SUPPLIERS}/${id}`,
        supplier as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Void a supplier
   */
  const voidSupplier = useCallback(
    async (id: string, reason?: string): Promise<Supplier> => {
      const queryParams: Record<string, string> = {};
      if (reason) {
        queryParams.reason = reason;
      }
      const result = await apiClient.delete<Supplier>(
        `${API_ROUTES.SUPPLIERS}/${id}`,
        undefined,
        queryParams,
      );
      if (!result) {
        throw new Error('Failed to void supplier: No response from server');
      }
      return result;
    },
    [apiClient],
  );

  /**
   * Restore a voided supplier
   */
  const restoreSupplier = useCallback(
    async (id: string): Promise<Supplier> => {
      return apiClient.patch<Supplier>(
        `${API_ROUTES.SUPPLIERS}/${id}/restore`,
        {},
      );
    },
    [apiClient],
  );

  /**
   * Get supplier history
   */
  const getSupplierHistory = useCallback(
    async (id: string): Promise<Supplier[]> => {
      return apiClient.get<Supplier[]>(`${API_ROUTES.SUPPLIERS}/${id}/history`);
    },
    [apiClient],
  );

  /**
   * Rollback supplier to a snapshot
   */
  const rollbackSupplier = useCallback(
    async (id: string, snapshotId: string): Promise<Supplier> => {
      return apiClient.patch<Supplier>(
        `${API_ROUTES.SUPPLIERS}/${id}/rollback/${snapshotId}`,
        {},
      );
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getAllSuppliers,
      getSupplierById,
      createSupplier,
      updateSupplier,
      voidSupplier,
      restoreSupplier,
      getSupplierHistory,
      rollbackSupplier,
    }),
    [
      getAllSuppliers,
      getSupplierById,
      createSupplier,
      updateSupplier,
      voidSupplier,
      restoreSupplier,
      getSupplierHistory,
      rollbackSupplier,
    ],
  );
}
